/**
 * Direction of travel.
 *
 * The important one is `stalled`. It does not mean she failed, and it does not
 * mean she is stuck — it means we ran an experiment and it did not move, which
 * is real information about the approach rather than about the child. So it is
 * labelled as the action it calls for ("new angle") and coloured as a signal to
 * read, not a warning to fear. Everything else in this file exists to keep that
 * one label from looking like an alarm.
 */

export type TrendKey = 'rising' | 'steady' | 'stalled' | 'regressing'

const TRENDS: Record<TrendKey, { label: string; description: string }> = {
  rising: {
    label: 'Rising',
    description: 'Moving up lately. Whatever you are doing here is working — keep it.',
  },
  steady: {
    label: 'Steady',
    description: 'Holding where she is. Solid and consistent, and often exactly right.',
  },
  stalled: {
    label: 'New angle',
    description:
      'We have tried this and it has not moved. That is worth knowing: it usually means the ' +
      'approach needs to change shape rather than that she needs more practice at the same thing.',
  },
  regressing: {
    label: 'Worth a look',
    description:
      'Recent observations sit below where she was. Usually that is a harder context, a tired ' +
      'week, or a skill being rebuilt on a better foundation — worth a look, not a worry.',
  },
}

export function isTrend(value?: string): value is TrendKey {
  return !!value && value in TRENDS
}

export function trendLabel(trend?: string): string | undefined {
  return isTrend(trend) ? TRENDS[trend].label : undefined
}

/** The plain-language version, for anywhere with room for a sentence. */
export function describeTrend(trend?: string): string | undefined {
  return isTrend(trend) ? TRENDS[trend].description : undefined
}

export function TrendBadge({
  trend,
  size = 'md',
  showLabel = true,
}: {
  trend?: string
  size?: 'sm' | 'md'
  showLabel?: boolean
}) {
  if (!isTrend(trend)) return null
  const { label } = TRENDS[trend]

  return (
    <span className={`trendbadge trendbadge-${size} trend-${trend}`}>
      <TrendIcon trend={trend} />
      {showLabel ? (
        <span className="trendbadge-label">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </span>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

/**
 * Four shapes that stay distinct without colour, so the meaning survives a
 * colour-blind reader and a greyscale print. Stalled is a fork in the road —
 * the picture of choosing another way — rather than a warning triangle.
 */
function TrendIcon({ trend }: { trend: TrendKey }) {
  const size = 13
  if (trend === 'rising') {
    return (
      <svg {...S} width={size} height={size}>
        <path d="M4 17L10 10l3.5 3.5L20 6" />
        <path d="M20 11V6h-5" />
      </svg>
    )
  }
  if (trend === 'steady') {
    return (
      <svg {...S} width={size} height={size}>
        <path d="M4 12h16" />
        <path d="M16 8l4 4-4 4" />
      </svg>
    )
  }
  if (trend === 'stalled') {
    return (
      <svg {...S} width={size} height={size}>
        <path d="M12 21V13" />
        <path d="M12 13L5 6" />
        <path d="M12 13l7-7" />
        <path d="M19 11V6h-5" />
      </svg>
    )
  }
  return (
    <svg {...S} width={size} height={size}>
      <path d="M4 7l6 7 3.5-3.5L20 18" />
      <path d="M20 13v5h-5" />
    </svg>
  )
}
